/**
 * PrivacyModal — Reads content from content.json.
 * Zero hardcoded prose. GPL-licensed shell.
 */

import { useEffect, useRef } from 'react';
import content from '../../content.json';
import type { SiteContent } from '../types';

const data = (content as unknown as SiteContent).privacyPolicy;

interface PrivacyModalProps {
  onClose: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  lock: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
  ),
  database: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>
  ),
  shield: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
  ),
};

export default function PrivacyModal({ onClose }: PrivacyModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8 bg-slate-950/90 backdrop-blur-md" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="privacy-title">
      <div className="bg-slate-900 rounded-[2.5rem] w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-y-auto custom-scrollbar border border-slate-800" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 px-8 py-6 border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm flex items-center justify-between z-10">
          <div className="flex items-center gap-4 text-emerald-500">
            {ICONS.lock}
            <h3 id="privacy-title" className="text-xl font-bold uppercase tracking-tight text-white">{data.title}</h3>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-full transition-all" aria-label="Close">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {/* Body */}
        <div className="p-8 md:p-12 space-y-12">
          <section className="space-y-10">
            {data.points.map((point, i) => (
              <div key={i} className="flex items-start gap-6">
                <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center shrink-0 border border-emerald-500/20" aria-hidden="true">
                  {ICONS[point.icon] || ICONS.shield}
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2 text-white">{point.title}</h4>
                  <p className="text-sm text-slate-400 leading-relaxed">{point.content}</p>
                </div>
              </div>
            ))}
          </section>
          <div className="flex justify-center pt-4">
            <button type="button" onClick={onClose} className="px-10 py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-xl shadow-emerald-600/20 hover:bg-emerald-500 transition-all active:scale-95">
              {data.closeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
